import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listReviews, approveReview, requestReviewChanges, type Review, type Page } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-gray-700 text-gray-300",
  IN_REVIEW: "bg-yellow-900/50 text-yellow-300",
  APPROVED: "bg-green-900/50 text-green-300",
  CHANGES_REQUESTED: "bg-orange-900/50 text-orange-300",
  REJECTED: "bg-red-900/30 text-red-400",
};

export function DeliveryReviewsPage() {
  const { token } = useAuth();
  const [data, setData] = useState<Page<Review> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listReviews(token, { status: statusFilter || undefined, limit: 50 })
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token, statusFilter]);

  const act = async (fn: () => Promise<Review>) => {
    try {
      setActing("loading");
      await fn();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Code Reviews</h1>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        {data != null && (
          <div className="space-y-3">
            <p className="text-gray-400 text-sm">{data.total} reviews</p>
            {data.items.map((review) => (
              <div key={review.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">{review.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[review.status] ?? "bg-gray-700 text-gray-300"}`}>{review.status}</span>
                    </div>
                    <p className="text-gray-500 text-xs mt-1">{review.artifactType}: {review.artifactRef}</p>
                    {review.comments && <p className="text-gray-400 text-sm mt-2 line-clamp-3">{review.comments}</p>}
                    {review.agentOutput && (
                      <details className="mt-2">
                        <summary className="text-xs text-blue-400 cursor-pointer">AI Analysis</summary>
                        <pre className="text-xs text-gray-400 mt-1 whitespace-pre-wrap bg-gray-900 rounded p-2 max-h-40 overflow-y-auto">{review.agentOutput}</pre>
                      </details>
                    )}
                  </div>
                  {(review.status === "IN_REVIEW" || review.status === "PENDING") && (
                    <div className="flex gap-2 shrink-0">
                      <button disabled={acting != null} onClick={() => act(() => approveReview(token!, review.id, review.version))} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50">Approve</button>
                      <button disabled={acting != null} onClick={() => {
                        const c = window.prompt("Comments:");
                        if (c) act(() => requestReviewChanges(token!, review.id, review.version, c));
                      }} className="px-3 py-1 text-xs bg-orange-700 hover:bg-orange-600 text-white rounded transition-colors disabled:opacity-50">Request Changes</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {data.items.length === 0 && <p className="text-gray-400 text-sm">No reviews found.</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
